import { useEffect, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useUiStore } from '@/store/ui.store';
import Athena from '@/pages/athena/Athena';

/**
 * LA VENTANA DE ATHENA.
 *
 * Pedido del 9-oct-2026, textual: *"Athena debe desplegarse con una ventana,
 * no así en full screen."* Antes el botón flotante navegaba a `/athena`, que
 * reemplazaba la pantalla entera: para preguntarle algo sobre una venta había
 * que salirse de la venta.
 *
 * Ahora es una ventana encima de lo que se esté viendo. En el teléfono, una
 * hoja que sube desde abajo: una ventana flotante de 420 px no cabe en 390.
 *
 * ═══ CERRARLA NO BORRA LA CHARLA ═══
 *
 * El historial de Athena vive en la pantalla, no en la base. Si la ventana se
 * desmontara al cerrarla, cada vistazo a la pantalla de atrás costaría la
 * conversación entera. Se monta la primera vez que se abre y después solo se
 * esconde.
 */
export function VentanaAthena() {
  const abierta = useUiStore((s) => s.athenaAbierta);
  const cerrar = useUiStore((s) => s.cerrarAthena);
  const [montada, setMontada] = useState(false);

  useEffect(() => {
    if (abierta) setMontada(true);
  }, [abierta]);

  useEffect(() => {
    if (!abierta) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cerrar();
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [abierta, cerrar]);

  if (!montada) return null;

  return (
    <section
      role="dialog"
      aria-label="Athena"
      aria-hidden={!abierta}
      className={cn(
        'fixed z-40 flex flex-col overflow-hidden border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900',
        // Teléfono: hoja desde abajo. Escritorio: ventana sobre el botón.
        'inset-x-0 bottom-0 h-[85dvh] rounded-t-2xl',
        'md:inset-x-auto md:bottom-24 md:right-4 md:h-[min(640px,calc(100dvh-8rem))] md:w-[420px] md:rounded-2xl',
        // `invisible` y no solo transparente: cerrada, sus botones no reciben el
        // tabulador ni los clics de lo que queda debajo.
        'origin-bottom-right transition-[opacity,transform,visibility] duration-200 motion-reduce:transition-none',
        abierta
          ? 'visible translate-y-0 scale-100 opacity-100'
          : 'invisible translate-y-4 scale-95 opacity-0',
      )}
    >
      <header className="flex shrink-0 items-center gap-2 border-b border-slate-200 px-3 py-2.5 dark:border-slate-700">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-violet-600 text-white">
          <Sparkles className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold leading-tight text-slate-900 dark:text-slate-50">
            Athena
          </h2>
          <p className="truncate text-xs text-slate-500 dark:text-slate-400">
            Tu secretaria de WhatsApp
          </p>
        </div>
        <button
          onClick={cerrar}
          aria-label="Cerrar Athena"
          // 44 px: en el teléfono la ventana tapa el botón flotante y esta «X» es la única salida.
          className="-my-1 grid h-11 w-11 shrink-0 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
        >
          <X className="h-4 w-4" />
        </button>
      </header>
      <div className="min-h-0 flex-1 p-3">
        <Athena />
      </div>
    </section>
  );
}
